import { useCallback, useEffect, useState } from "react";
import { Form, formBlockMap } from "@form/components/form/modules";
import useForm from "@form/hooks/useForm";
import { useRouter } from "next/router";
import { StarIcon, GiftIcon, EyeIcon } from "@form/components/common/icons";
import Link from "next/link";
import { Button, ScrollArea } from "@mantine/core";
import { googleMapLink, googleCalendarLink } from "@form/utils/format";
import useVerifyCampaign from "@form/hooks/useVerifyCampaign";
import {
  checkCampaignAvailable,
  processServerForm,
} from "@form/services/campaign";
import RedirectModal from "@form/components/campaign/RedirectModal";
import useCampaignResponseStatus from "@form/hooks/useCampaignResponseStatus";
import SuccessModal from "@form/components/common/SuccessModal";
import store, { actions, useTypedSelector } from "@/stores";
import PageHead from "@/components/common/PageHead";
import { TDK } from "@form/config/config";
import useAccount from "@/hooks/useAccount";
import useEditMessage from "@form/hooks/useEditMessage";

import WalletVerify from "./WalletVerify";
import TwitterVerify from "./TwitterVerify";
import TelegramVerify from "./TelegramVerify";
import EmailVerify from "./EmailVerify";
import QuestionVerify from "./QuestionVerify";
import DiscordVerify from "./DiscordVerify";
import SourceTrack from "./SourceTrack";
import SendingmeVerify from "./SendingmeVerify";

import type { FunctionComponent } from "react";

const CampaignEditor: FunctionComponent<{
  embed: boolean;
}> = ({ embed }) => {
  const router = useRouter();
  const {
    verified,
    submit,
    submiting,
    form: userForm,
    submitError,
  } = useVerifyCampaign();
  const [showRedirect, setShowRedirect] = useState(false);
  const { address, isLogin } = useAccount();
  const { form: localForm, error: localError } = useForm(
    router.query.id as string
  );

  const { activeVerify } = useTypedSelector((state) => {
    return {
      activeVerify: state.campaignVerify.activeVerify,
    };
  });

  const activeFrom = localForm;
  const activeError = localError;

  const { status, setStatus } = useCampaignResponseStatus(
    router.query.id as string
  );

  const viewMode = router.query.mode || "default";

  const [successOpened, setSuccessOpened] = useState(false);

  const needVerify = activeVerify === activeFrom?.id;

  useEffect(() => {
    if (isLogin) {
      const timer = setInterval(() => {
        store.dispatch(actions.account.fetchUserInfo());
      }, 3000);
      return () => {
        clearInterval(timer);
      };
    }
  }, [isLogin]);

  useEffect(() => {
    if (router.query.theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    return () => {
      document.documentElement.classList.remove("dark");
    };
  }, [router]);

  const blocks = formBlockMap(activeFrom);

  const needRedirect = !!blocks?.redirect;

  const showInfoBlock =
    !!blocks?.max_entries ||
    !!blocks?.website ||
    !!blocks?.event_date ||
    !!blocks?.location;

  const isPreview = router.route === "/forms/preview";

  const sectionClass =
    "pb-6 mb-6 border-b border-b-gray_f3 last:pb-0 last:mb-0 last:border-none dark:border-b-[#192844]";

  const navBack = useCallback(() => {
    router.back();
  }, []);

  const onSubmit = useCallback(async () => {
    if (!activeFrom?.id) {
      return;
    }
    store.dispatch(actions.campaignVerify.setActiveVerifyForm(activeFrom.id));
    if (!verified) {
      return;
    }
    const blocks = formBlockMap(activeFrom);
    console.log("userForm", userForm);
    const res = await submit({
      campaign_id: activeFrom.id,
      context: userForm,
      campaign_name: blocks?.title.values.title,
    });
    if (res) {
      setStatus(res);
      store.dispatch(
        actions.campaignVerify.changeForm({
          field: "id",
          value: res.id,
        })
      );
      setSuccessOpened(true);
      if (needRedirect) {
        setShowRedirect(true);
      }
      if (embed && window.parent) {
        window.parent.postMessage(
          {
            type: "submit",
            data: {
              context: userForm,
              user_address: address,
              id: activeFrom.id,
            },
          },
          "*"
        );
      }
    }
  }, [activeFrom, userForm, needRedirect, embed, verified]);

  useEditMessage({
    blocks,
    status,
  });

  const campaginNotAvaliable = activeFrom
    ? !checkCampaignAvailable(activeFrom)
    : false;

  const tdk = {
    ...TDK,
  };
  if (blocks?.title?.values?.title) {
    tdk.title = blocks?.title?.values?.title;
  }
  if (blocks?.description?.values?.description) {
    tdk.description = blocks?.description?.values?.description;
  }
  return (
    <>
      <PageHead tdk={tdk}></PageHead>
      <div
        className={`flex justify-center mb-8 ${
          campaginNotAvaliable ? "" : "hidden"
        } mt-8 sm:mt-0`}
      >
        <div className="text-header text-dark1 font-bold text-center">
          <div>This form is currently closed. </div>
          <div>Contact form owner for additional information.</div>
        </div>
      </div>
      <div
        className={`flex justify-center mb-8 ${
          activeError ? "" : "hidden"
        } mt-8 sm:mt-0`}
      >
        <div className="text-header text-dark1 font-bold text-center">
          <div>{activeError}</div>
          <div>Contact form owner for additional information.</div>
        </div>
      </div>
      <div
        className={`${
          isPreview ? "" : "hidden"
        } flex items-center justify-between py-2 px-7 mb-4 shadow-[0px_4px_4px_rgba(0,0,0,0.1)] bg-white rounded-lg`}
      >
        <div className="flex items-center">
          <EyeIcon></EyeIcon>
          <span className="ml-2 text-headerL font-bold text-dark1">
            Preview mode
          </span>
        </div>
        <div className="flex items-center text-primary font-bold text-header">
          <div className="action" onClick={navBack}>
            Exit
          </div>
        </div>
      </div>
      <div
        className={`${
          embed
            ? "w-full bg-white e-form:pr-0 e-form:h-[80vh] e-form:max-h-[833px] e-form:flex"
            : "bg-white s-form:shadow-[15px_4px_100px_rgba(0,0,0,0.1)] s-form:rounded-lg s-form:p-10 s-form:pr-0 s-form:h-[80vh] s-form:max-h-[833px] s-form:flex"
        }  p-4  transition-all  ${!blocks ? "blur-sm" : ""} pb-20 sm:pb-0 ${
          viewMode === "s" ? "s-form:max-h-full w-full bg-transparent" : ""
        } dark:bg-[#151C2B]`}
      >
        <div
          className={`${
            campaginNotAvaliable ? "bg-gray_f9" : "bg-primary/80"
          } ${
            embed
              ? "e-form:w-full e-form:mr-10 flex-1"
              : "s-form:w-[400px] s-form:mr-10"
          } max-h-[608px] py-14 px-10 rounded-lg  flex-shrink-0  flex flex-col ${
            viewMode === "s" ? "hidden" : ""
          } dark:bg-[#1D283C]`}
        >
          {blocks?.reward?.values.type &&
            blocks?.reward?.values.type !== "empty" && (
              <div className="flex-shrink-0 w-[103px] py-3  mb-5 bg-[#FFE767] text-bodyS font-bold text-dark1 rounded-sm text-center relative dark:bg-[#FFD0AD]">
                {blocks?.reward?.rewardName()?.toUpperCase()}
                <GiftIcon className="absolute -left-1 -top-1"></GiftIcon>
                <StarIcon className="w-2 h-2 absolute left-1 bottom-1"></StarIcon>
                <StarIcon className="absolute left-4 bottom-3 fill-[#FFD600]"></StarIcon>
                <StarIcon className="absolute right-4 top-1"></StarIcon>
                <StarIcon className="w-2 h-2 absolute right-1 top-2 fill-[#FFD600] rotate-12"></StarIcon>
              </div>
            )}
          <div
            className={`flex-shrink-0 text-2xl font-bold ${
              campaginNotAvaliable ? "text-dark1" : "text-white"
            } mb-7`}
          >
            {blocks?.title?.values?.title}
          </div>
          <div
            className={`flex-shrink-0  ${
              campaginNotAvaliable
                ? "bg-gray_f0 text-dark1"
                : "bg-[#5194FF] text-white"
            } mb-7 p-4 rounded-lg text-bodyS font-bold ${
              showInfoBlock ? "block" : "hidden"
            } dark:bg-[#161E30]`}
          >
            {blocks?.max_entries?.module?.icon && (
              <div className="flex items-center mb-2 last:mb-0">
                <blocks.max_entries.module.icon
                  className={`w-4 h-4 mr-3 ${
                    campaginNotAvaliable ? "fill-dark1" : ""
                  }`}
                ></blocks.max_entries.module.icon>
                <span>{blocks.max_entries.values.amount} Entries </span>
              </div>
            )}
            {blocks?.website?.module?.icon && (
              <div className="flex items-center mb-2 last:mb-0">
                <blocks.website.module.icon
                  className={`w-4 h-4 mr-3 flex-shrink-0 ${
                    campaginNotAvaliable ? "fill-dark1" : ""
                  }`}
                ></blocks.website.module.icon>
                <Link href={blocks.website.values.link} passHref>
                  <a
                    className="action flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
                    target="_blank"
                    data-track="click_website"
                    data-track-params={`{"form_id": "${router.query.id}"}`}
                  >
                    {blocks.website.values.link}
                  </a>
                </Link>
              </div>
            )}
            {blocks?.event_date?.module?.icon && (
              <div className="flex items-center mb-2 last:mb-0">
                <blocks.event_date.module.icon
                  className={`w-4 h-4 mr-3 ${
                    campaginNotAvaliable ? "fill-dark1" : ""
                  }`}
                ></blocks.event_date.module.icon>
                <Link
                  href={googleCalendarLink({
                    title: blocks.title.values.title,
                    description: blocks.description.values.description,
                    location: blocks.location?.values?.location,
                    start: new Date(blocks.event_date.values.start),
                    end: new Date(blocks.event_date.values.end),
                  })}
                  passHref
                >
                  <a className="action" target="_blank">
                    {blocks.event_date.dateDesc()}
                  </a>
                </Link>
              </div>
            )}
            {blocks?.location?.module?.icon && (
              <div className="flex items-center mb-2 last:mb-0">
                <blocks.location.module.icon
                  className={`w-4 h-4 mr-3 flex-shrink-0 ${
                    campaginNotAvaliable ? "fill-dark1" : ""
                  }`}
                ></blocks.location.module.icon>
                {blocks.location.values.type === "online" && (
                  <Link href={blocks.location.values.link as string} passHref>
                    <a
                      data-track="open_link"
                      data-track-params={`{"form_id": "${router.query.id}"}`}
                      className="action flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
                      target="_blank"
                    >
                      {blocks.location.values.link}
                    </a>
                  </Link>
                )}
                {blocks.location.values.type !== "online" && (
                  <Link
                    href={googleMapLink(
                      blocks.location.values.location as string
                    )}
                    passHref
                  >
                    <a
                      className="action whitespace-pre-wrap"
                      target="_blank"
                      data-track="open_link"
                      data-track-params={`{"form_id": "${router.query.id}"}`}
                    >
                      {blocks.location.locationDesc()}
                    </a>
                  </Link>
                )}
              </div>
            )}
          </div>
          <ScrollArea
            className={`flex-1 text-bodyS ${
              campaginNotAvaliable ? "text-dark1" : "text-gray_f3"
            }  whitespace-pre-wrap`}
            scrollbarSize={6}
            styles={{
              scrollbar: {
                '&[data-orientation="vertical"] .mantine-ScrollArea-thumb': {
                  backgroundColor: "#3C86FB",
                },
                "&, &:hover": {
                  background: "#E9F1FF",
                  borderRadius: "8px",
                },
              },
            }}
          >
            {blocks?.description?.values?.description}
          </ScrollArea>
        </div>
        <div className={`h-full flex flex-col flex-1`}>
          <ScrollArea
            className={`flex-1 pt-10 pb-4 ${
              embed
                ? "!overflow-visible e-form:!overflow-hidden e-form:pr-7 e-form:mr-3"
                : "s-form:pr-7 s-form:mr-3"
            } ${viewMode === "s" ? "pt-0" : ""}`}
            scrollbarSize={6}
            styles={{
              scrollbar: {
                '&[data-orientation="vertical"] .mantine-ScrollArea-thumb': {
                  backgroundColor: "#3C86FB",
                },
                "&, &:hover": {
                  background: "#E0E1E1",
                  borderRadius: "8px",
                },
              },
            }}
          >
            <div className={sectionClass}>
              <WalletVerify
                available={!campaginNotAvaliable}
                holding={blocks?.holding}
                needVerify={needVerify}
              ></WalletVerify>
            </div>
            {blocks?.twitter?.module?.icon && (
              <div className={sectionClass}>
                <TwitterVerify
                  available={!campaginNotAvaliable}
                  form={blocks.twitter}
                  needVerify={needVerify}
                  submitError={submitError}
                ></TwitterVerify>
              </div>
            )}
            {blocks?.discord?.module?.icon && (
              <div className={sectionClass}>
                <DiscordVerify
                  available={!campaginNotAvaliable}
                  form={blocks.discord}
                  needVerify={needVerify}
                  submitError={submitError}
                ></DiscordVerify>
              </div>
            )}
            {blocks?.sendingme?.module?.icon && (
              <div className={sectionClass}>
                <SendingmeVerify
                  available={!campaginNotAvaliable}
                  form={blocks.sendingme}
                  needVerify={needVerify}
                  submitError={submitError}
                ></SendingmeVerify>
              </div>
            )}
            {/* {blocks?.telegram?.module?.icon && (
              <div className={sectionClass}>
                <TelegramVerify available={!campaginNotAvaliable} form={blocks.telegram}></TelegramVerify>
              </div>
            )} */}
            {blocks?.email?.module?.icon && (
              <div className={sectionClass}>
                <EmailVerify
                  available={!campaginNotAvaliable}
                  form={blocks.email}
                  needFormVerify={needVerify}
                  submitError={submitError}
                ></EmailVerify>
              </div>
            )}
            {blocks?.question?.module?.icon && (
              <div className={sectionClass}>
                <QuestionVerify
                  available={!campaginNotAvaliable}
                  quesion={blocks.question}
                  needVerify={needVerify}
                ></QuestionVerify>
              </div>
            )}
          </ScrollArea>
          <div className="flex justify-center mt-5">
            {status ? (
              <Button
                classNames={{
                  inner: "text-body w-[100px]",
                  root: "w-[155px] opacity-30",
                }}
                size="md"
                radius="xl"
              >
                Submited
              </Button>
            ) : (
              <Button
                classNames={{
                  inner: "text-body w-[100px]",
                  root: "w-[155px]",
                }}
                size="md"
                radius="xl"
                loading={submiting}
                disabled={!campaginNotAvaliable ? isPreview || status : true}
                onClick={onSubmit}
              >
                Submit
              </Button>
            )}
          </div>
        </div>
      </div>
      <SourceTrack></SourceTrack>
      <RedirectModal
        opened={showRedirect}
        setOpened={setShowRedirect}
        link={blocks?.redirect?.values.link}
      ></RedirectModal>
      <div className={`fixed w-[300px] right-[64px] bottom-[144px] z-[1500]`}>
        <SuccessModal
          opened={successOpened}
          setOpened={setSuccessOpened}
          desc="Form successfully submitted"
          className="bg-brand2 !text-white rounded-lg"
        ></SuccessModal>
      </div>
    </>
  );
};

export default CampaignEditor;
